import { defineStore } from 'pinia'
import { nextTick, reactive, ref } from 'vue'
import { ContactForm, ValidationState } from '~concierge/types/FormTypes'
import { isValidNumber } from 'libphonenumber-js'
import { useAppointmentStore } from './appointmentStore'
import { useForm } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import { getActiveLanguage, loadLanguageAsync } from 'laravel-vue-i18n'

export const useSubmissionFormStore = defineStore({
  id: 'submissionForm',
  state: () => ({
    submittedSuccessfully: ref<boolean>(false),
    formData: useForm<ContactForm>({
      first_name: '',
      last_name: '',
      email: '',
      cellphone: '',
      date: '',
      time: '',
      vehicle_id: '',
      adviser_id: '',
      language: getActiveLanguage() ?? 'fr',
      consent: false,
      captcha_token: null
    }),
    validationState: reactive<ValidationState>({
      first_name: [],
      last_name: [],
      email: [],
      cellphone: [],
      consent: []
    }),
    focusState: reactive<Record<string, boolean>>({
      first_name: false,
      last_name: false,
      email: false,
      cellphone: false
    }),
    appointmentErrors: ref<Array<string>>([]),
    successResponse: reactive<Record<string, string | Array<string>>>({
      type: '',
      greetings: '',
      title: '' as string,
      content: [] as Array<string>
    }),
    hasTheAbilityToGoBackToSelection: ref<boolean>(false)
  }),

  getters: {
    isFormValid(state): boolean {
      return (
        Object.values(state.focusState).every(field => field === true) &&
        Object.values(state.validationState).every(field => !field.length) &&
        state.formData.consent
      )
    },
    hasFormSuccess(state): boolean {
      return state.successResponse?.type.length !== 0 && state.submittedSuccessfully
    },
    formLanguage(state): string {
      return state.formData.language
    }
  },
  actions: {
    setLocale(locale: string): void {
      this.formData.language = locale
    },
    resetValidationState(): void {
      this.validationState = {
        first_name: [],
        last_name: [],
        email: [],
        cellphone: [],
        consent: []
      }
      this.appointmentErrors = []
    },
    resetAppointmentData(): void {
      this.hasTheAbilityToGoBackToSelection = false
      this.formData.reset('date', 'time', 'vehicle_id', 'adviser_id')
    },
    async submitForm(submissionRoute: string): Promise<void> {
      try {
        this.hasTheAbilityToGoBackToSelection = false
        this.appointmentErrors = []
        this.buildFormData()
        const dateTime = {
          date: this.formData.date,
          time: this.formData.time
        }
        this.formData.post(submissionRoute, {
          preserveScroll: true,
          onCancelToken: cancelToken => {},
          onCancel: () => {},
          onBefore: visit => {},
          onStart: visit => {
            console.table(dateTime)
          },
          onProgress: progress => {
            console.log('onProgress')
          },
          onSuccess: page => {
            this.submittedSuccessfully = true
            const success = page.props.successResponse as Record<string, string | Array<string>>

            this.successResponse = success
          },
          onError: validationErrors => {
            for (const key in validationErrors) {
              if (Object.hasOwn(this.validationState, key)) {
                this.validationState[key].push(validationErrors[key])
              } else {
                this.appointmentErrors.push(validationErrors[key])
              }
              if (['date', 'time', 'adviser_id', 'vehicle_id'].includes(key)) {
                this.hasTheAbilityToGoBackToSelection = true
              }
            }
          },
          onFinish: visit => {}
        })
      } catch (error: unknown | Error) {
        console.error('submitForm Error submitting form:', error)
      }
    },
    validate(inputName: keyof ContactForm): void {
      switch (inputName) {
        case 'email':
          this.validateEmail()
          break
        case 'cellphone':
          this.validatePhone()
          break
        case 'first_name':
          this.validateFirstName()
          break
        case 'last_name':
          this.validateLastName()
          break
        case 'consent':
          this.validateConsent()
          break
        default:
          console.error(`Validation not implemented for field: ${inputName}`)
      }
    },
    buildFormData(): void {
      const appointmentFields = useAppointmentStore().buildAppointmentForSubmission

      for (const key in appointmentFields) {
        if (Object.hasOwn(appointmentFields, key)) {
          this.formData[key] = appointmentFields[key] ?? ''
        }
      }
    },
    validatePhone(): void {
      this.validationState.cellphone = []
      if (!isValidNumber(this.formData.cellphone, 'CA')) {
        this.validationState.cellphone.push(trans('submission_form.form.validations.cellphone'))
      }
    },
    validateEmail(): void {
      this.validationState.email = []
      if (this.formData.email.length > 0) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.formData.email)) {
          this.validationState.email.push(trans('submission_form.form.validations.email'))
        }
      }
    },
    validateFirstName(): void {
      this.validationState.first_name = []

      if (this.formData.first_name?.length < 3) {
        this.validationState.first_name.push(trans('submission_form.form.validations.first_name'))
      }
    },
    validateLastName(): void {
      this.validationState.last_name = []

      if (this.formData.last_name?.length < 3) {
        this.validationState.last_name.push(trans('submission_form.form.validations.last_name'))
      }
    },
    validateConsent(): void {
      this.validationState.consent = []

      if (!this.formData.consent) {
        this.validationState.consent.push(trans('submission_form.form.validations.consent'))
      }
    }
  }
})
